import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Truck } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const Carrier = () => {
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  const [carriers, setCarriers] = useState([]);
  const [filteredCarriers, setFilteredCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [activeOnly, setActiveOnly] = useState(false);
  
  const [formData, setFormData] = useState({
    CarrierID: null,
    Carrier: '',
    IsActive: true
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchCarriers();
  }, [companyId, yearId, activeOnly]);

  useEffect(() => {
    filterCarriers();
  }, [searchTerm, carriers]);

  const fetchCarriers = async () => {
    setLoading(true);
    try {
      const params = activeOnly ? { table: 'carrier', active: '1' } : { table: 'carrier' };
      const response = await apiClient.get('/LookupMaster.php', { params });
      
      if (response.data.success) {
        setCarriers(response.data.data || []);
      } else {
        alert('Failed to fetch carriers');
      }
    } catch (error) {
      console.error('Error fetching carriers:', error);
      alert('Error fetching carriers');
    } finally {
      setLoading(false);
    }
  };

  const filterCarriers = () => {
    if (!searchTerm.trim()) {
      setFilteredCarriers(carriers);
    } else {
      const filtered = carriers.filter(carrier =>
        carrier.Carrier.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCarriers(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.Carrier.trim()) {
      newErrors.Carrier = 'Carrier name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSubmitLoading(true);
    try {
      const payload = {
        ...formData,
        IsActive: formData.IsActive ? 1 : 0
      };
      
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/LookupMaster.php?table=carrier', payload);
      
      if (response.data.success) {
        alert(editMode ? 'Carrier updated successfully!' : 'Carrier created successfully!');
        setShowModal(false);
        resetForm();
        fetchCarriers();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error saving carrier:', error);
      alert('Error saving carrier: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = async (carrier) => {
    try {
      const response = await apiClient.get(`/LookupMaster.php?table=carrier&id=${carrier.CarrierID}`);
      if (response.data.success) {
        setFormData({
          CarrierID: response.data.data.CarrierID,
          Carrier: response.data.data.Carrier,
          IsActive: response.data.data.IsActive === 1
        });
        setEditMode(true);
        setShowModal(true);
        setErrors({});
      }
    } catch (error) {
      console.error('Error loading carrier:', error);
      alert('Error loading carrier details');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this carrier?')) return;
    
    try {
      const response = await apiClient.delete('/LookupMaster.php?table=carrier', {
        data: { CarrierID: id }
      });
      
      if (response.data.success) {
        alert('Carrier deleted successfully!');
        fetchCarriers();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting carrier:', error);
      alert('Error deleting carrier');
    }
  };

  const resetForm = () => {
    setFormData({ CarrierID: null, Carrier: '', IsActive: true });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Carrier Master</h2>
          <p className="subtitle">Manage carrier carriers</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Carrier
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search carriers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        
        <div className="filter-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            <span>Active Only</span>
          </label>
        </div>

        <div className="toolbar-info">
          Total: {filteredCarriers.length}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading carriers...</div>
      ) : filteredCarriers.length === 0 ? (
        <div className="empty-state">
          <p>No carriers found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Carrier</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCarriers.map((carrier) => (
                <tr key={carrier.CarrierID}>
                  <td className="font-mono">{carrier.CarrierID}</td>
                  <td className="font-semibold">{carrier.Carrier}</td>
                  <td>
                    {carrier.IsActive === 1 ? (
                      <span className="status-badge active">Active</span>
                    ) : (
                      <span className="status-badge inactive">Inactive</span>
                    )}
                  </td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(carrier)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(carrier.CarrierID)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Carrier' : 'Add New Carrier'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section">
                <h4>
                  <Truck size={18} />
                  Carrier Information
                </h4>
                
                <div className="form-group">
                  <label htmlFor="Carrier">
                    Carrier Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="Carrier"
                    value={formData.Carrier}
                    onChange={(e) => setFormData({ ...formData, Carrier: e.target.value })}
                    maxLength="255"
                    placeholder="e.g., Emirates, Qatar Airways"
                    className={errors.Carrier ? 'error' : ''}
                    autoFocus
                  />
                  {errors.Carrier && (
                    <span className="error-message">{errors.Carrier}</span>
                  )}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.IsActive}
                      onChange={(e) => setFormData({ ...formData, IsActive: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Carrier;